import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { merge } from "lodash";

import Modals from "../../common/modal";
import { ltrim } from "../../../util/helper";
import {
  asyncCreateSupplier,
  asyncEditSupplier,
  asyncGetSupplier,
} from "../../../redux/slices/supplierSlice";
import { SuppliersFormSchema } from "../../../validators/formSchema";

const { TextArea } = Input;

const SupplierModal = ({ openModal, setOpenModal, messageApi, modalData }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { loading, pagination, editLoading } = useSelector((state) => ({
    loading: state.supplier.createSupplier.loading,
    pagination: state.supplier.getSupplier.pagination,
    editLoading: state.supplier.editSupplier.loading,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const [prevLat, setPrevLat] = useState("");
  const [prevLong, setPrevLong] = useState("");
  const [isLatLongChanged, setIsLatLongChanged] = useState(false);

  useEffect(() => {
    if (modalData?.data) {
      setPrevLat(modalData.data?.latitude);
      setPrevLong(modalData.data?.longitude);
    }
  }, [modalData]);

  const handleLatLongChange = (values) => {
    const isLatChanged = values.latitude !== prevLat;
    const isLongChanged = values.longitude !== prevLong;
    setIsLatLongChanged(isLatChanged || isLongChanged);
  };

  const footerElem = [
    <div className="modal_footer">
      <Button
        key="submit"
        loading={modalData.mode === "add" ? loading : editLoading}
        disabled={modalData.mode === "edit" && !isDirty}
        onClick={() => {
          if (formRef.current) {
            formRef.current.handleSubmit();
          }
        }}
      >
        {modalData.mode === "add" ? "Submit" : "Update"}
      </Button>
      <Button key="back" onClick={() => setOpenModal(false)}>
        Cancel
      </Button>
    </div>,
  ];

  const initialValue = {
    code: "",
    country: "",
    state: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
  };

  return (
    <Modals
      title={
        modalData.mode === "edit" ? "Update Supplier" : "Create New Supplier"
      }
      open={openModal}
      setOpen={setOpenModal}
      footer={footerElem}
      width={700}
    >
      <Formik
        innerRef={formRef}
        validationSchema={SuppliersFormSchema}
        initialValues={
          modalData?.mode
            ? merge(initialValue, { ...modalData.data })
            : initialValue
        }
        onSubmit={(values) => {
          const trimValue = {
            code: values.code?.trimEnd() || "",
            country: values.country?.trimEnd() || "",
            state: values.state?.trimEnd() || "",
            city: values.city?.trimEnd() || "",
            address: values.address?.trimEnd() || "",
            latitude: values.latitude?.trimEnd() || "",
            longitude: values.longitude?.trimEnd() || "",
          };
          const newValues = {
            ...trimValue,
            id: values.id,
            isLatLongChanged: isLatLongChanged,
          };

          if (modalData.mode === "add") {
            dispatch(asyncCreateSupplier(trimValue)).then((res) => {
              if (res.payload?.status === 201) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message ||
                    "Supplier Created Successfully",
                });
                dispatch(
                  asyncGetSupplier({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          } else {
            dispatch(asyncEditSupplier(newValues)).then((res) => {
              console.log(res);
              if (res.payload?.status === 200) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message ||
                    "Supplier Updated Successfully",
                });
                dispatch(
                  asyncGetSupplier({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          touched,
          setDirty,
        }) => (
          <div className="custom-scrollbar form_div">
            {/* <Divider style={{ margin: "10px 0" }} /> */}
            <Form
              onFinish={(e) => {
                setDirty(false);
                handleSubmit(e);
              }}
            >
              <Form.Item style={{ margin: "0" }}>
                <label htmlFor="code1" className="form-label required">
                  Code
                </label>
                <Input
                  id="code1"
                  type="text"
                  name="code"
                  className="form-input"
                  placeholder="Enter code"
                  onChange={(e) => {
                    if (handleChange) {
                      handleChange(e);
                      setIsDirty(true);
                    }
                  }}
                  value={values?.code ? ltrim(values?.code) : ""}
                  status={`${(errors.code && touched.code && "error") || ""}`}
                />
              </Form.Item>
              {errors.code && touched.code && (
                <small id="code-help" className="error-message">
                  {errors.code}
                </small>
              )}
              <Form.Item style={{ margin: "0" }}>
                <label htmlFor="address1" className="form-label required">
                  Address
                </label>
                <TextArea
                  rows={2}
                  id="address1"
                  type="text"
                  name="address"
                  className="form-input"
                  placeholder="Enter address"
                  onChange={(e) => {
                    if (handleChange) {
                      handleChange(e);
                      setIsDirty(true);
                    }
                  }}
                  value={values?.address ? ltrim(values?.address) : ""}
                  status={`${
                    (errors.address && touched.address && "error") || ""
                  }`}
                />
              </Form.Item>
              {errors.address && touched.address && (
                <small id="address-help" className="error-message">
                  {errors.address}
                </small>
              )}
              <div className="form_flex">
                <div>
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="city1" className="form-label required">
                      City
                    </label>
                    <Input
                      id="city1"
                      type="text"
                      name="city"
                      className="form-input"
                      placeholder="Enter city"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.city ? ltrim(values?.city) : ""}
                      status={`${
                        (errors.city && touched.city && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.city && touched.city && (
                    <small id="city-help" className="error-message">
                      {errors.city}
                    </small>
                  )}
                </div>
                <div>
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="state1" className="form-label required">
                      State
                    </label>
                    <Input
                      id="state1"
                      type="text"
                      name="state"
                      className="form-input"
                      placeholder="Enter state"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.state ? ltrim(values?.state) : ""}
                      status={`${
                        (errors.state && touched.state && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.state && touched.state && (
                    <small id="state-help" className="error-message">
                      {errors.state}
                    </small>
                  )}
                </div>
                <div>
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="country1" className="form-label required">
                      Country
                    </label>
                    <Input
                      id="country1"
                      type="text"
                      name="country"
                      className="form-input"
                      placeholder="Enter country"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.country ? ltrim(values?.country) : ""}
                      status={`${
                        (errors.country && touched.country && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.country && touched.country && (
                    <small id="country-help" className="error-message">
                      {errors.country}
                    </small>
                  )}
                </div>
              </div>
              <div className="form_flex_two">
                <div className="form_flex_two_left">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="latitude1" className="form-label required">
                      Latitude
                    </label>
                    <Input
                      id="latitude1"
                      type="text"
                      name="latitude"
                      className="form-input"
                      placeholder="Enter latitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            latitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.latitude ? ltrim(values?.latitude) : ""}
                      status={`${
                        (errors.latitude && touched.latitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.latitude && touched.latitude && (
                    <small id="latitude-help" className="error-message">
                      {errors.latitude}
                    </small>
                  )}
                </div>
                <div className="form_flex_two_right">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="longitude1" className="form-label required">
                      Longitude
                    </label>
                    <Input
                      id="longitude1"
                      type="text"
                      name="longitude"
                      className="form-input"
                      placeholder="Enter longitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            longitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.longitude ? ltrim(values?.longitude) : ""}
                      status={`${
                        (errors.longitude && touched.longitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.longitude && touched.longitude && (
                    <small id="longitude-help" className="error-message">
                      {errors.longitude}
                    </small>
                  )}
                </div>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    </Modals>
  );
};

export default SupplierModal;
